import { RealtimeRole } from '@doxyme/realtime';
import { createAsyncThunk } from '@reduxjs/toolkit';
import type { AppDispatch, RootState } from '../../stores/redux';
import { track, TrackedEvents } from '../tracking';
import { type SaveCallRatingInput, sessionApi } from '../../services/session';
import { subscriptions } from '../../services/subscriptions';
import { getCallDetailsForSurvey } from './postCallSurvey.selectors';
import { getApolloSessionDetails } from '../apolloSession/apolloSession.selectors';
import { getIsAnimationDisabled } from '../uiState/uiState.selectors';
import { getGPUData } from '../../shared/deviceCapabilities/getGpuData';
import { logger } from './logger';
import { session } from '../../webrtc/session';
import { getIsProvider } from '../realtime/realtime.selectors';
import { pubSubStatusStats } from '../../hooks/useTracking/pubSubStatusStats';
import { otExceptionsStats } from '../../hooks/useTracking/otExceptionsStats';

export type SurveyValues = {
  rating: number;
  problems: string[];
  comment: string;
};

export const submitPostCallSurvey = createAsyncThunk<
  void,
  SurveyValues,
  { state: RootState; dispatch: AppDispatch }
>('postCallSurvey/submit', async (values, { getState, dispatch }) => {
  const callDetails = getCallDetailsForSurvey(getState());
  if (!callDetails) {
    return;
  }

  const { dbSessionId, sessionId, noSoundDetected, vendor, callType } =
    callDetails;

  const isProvider = getIsProvider(getState());

  if (callType === 'phone' && !isProvider) {
    return;
  }

  const problems = values.problems.map((problem) =>
    problem.replaceAll('_', ' '),
  );
  const comment = values.comment.trim();

  const logToDataDog = (): void => {
    const rating = values.rating;

    const formattedProblems = [...problems];

    if (comment.length > 0) {
      formattedProblems.push(`otherissue/${comment}`);
    }

    // TODO: ideally, we match the problems to the original dokbot categorised values
    logger.info('callRated', {
      rating,
      callType,
      problems: formattedProblems,
      metrics: session.getMetrics(),
      ...pubSubStatusStats.finalizeAndGetStats(),
      otExceptionsStats: otExceptionsStats.finalizeAndGetStats(),
    });
  };

  const save = async (): Promise<void> => {
    const logProps = { callDetails, values };

    try {
      const input: SaveCallRatingInput = {
        sessionId: dbSessionId,
        channel: sessionId,
        rating: values.rating,
        data: problems,
        comment,
        noSoundDetected,
      };

      if (isProvider) {
        await dispatch(
          sessionApi.endpoints.SaveCallRatingFromProvider.initiate({ input }),
        ).unwrap();
      } else {
        const { roomId } = getApolloSessionDetails(getState());
        await dispatch(
          sessionApi.endpoints.SaveCallRatingFromPatient.initiate({
            input: { ...input, ...(roomId && { roomId }) },
          }),
        ).unwrap();
      }

      logger.info('[postCallSurvey/submit]', logProps);
    } catch {
      logger.error('[postCallSurvey/submit]', logProps);
    }
  };

  const sendToSegment = async (): Promise<void> => {
    const { userId, roomId, role } = getApolloSessionDetails(getState());

    const { tier: gpuTier } = await getGPUData();
    const isAnimationDisabled = getIsAnimationDisabled(getState());

    let subscriptionType = undefined;

    if (Boolean(userId)) {
      const { data } = await dispatch(
        subscriptions.endpoints.getSubscriptionType.initiate({
          userId: String(userId),
        }),
      );
      subscriptionType = data?.subscriptionType;
    }

    void track(TrackedEvents.CallRated, {
      userType: role?.toLowerCase(),
      userId,
      planType: subscriptionType,
      domain: null,
      clinicId: null,
      roomId:
        typeof roomId !== 'undefined' ? Number.parseInt(roomId) : undefined,
      sessionId: dbSessionId,
      channel: sessionId,
      callType,
      noSoundDetected,
      rating: values.rating,
      data: problems,
      comment,
      isProviderFeedback: role === RealtimeRole.Provider,
      surveyType: 'apollo',
      vendor,
      isApollo: true,
      gpuTier,
      isAnimationDisabled,
      ...pubSubStatusStats.finalizeAndGetStats(),
    });
  };

  void save();
  logToDataDog();
  void sendToSegment();
});
